import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/site/BrandLogo";
import { useLang } from "@/lib/i18n";

const MaskLine = ({ children, delay = 0 }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
      style={{ display: "inline-block" }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useLang();
  const { scrollY } = useScroll();
  const titleY = useTransform(scrollY, [0, 800], [0, -60]);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative isolate min-h-[92vh] w-full overflow-hidden bg-[color:var(--ivory)]"
    >
      {/* Blooms */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-40 h-[60vh] w-[60vh] rounded-full opacity-60 blur-3xl bloom"
        style={{
          background:
            "radial-gradient(closest-side, rgba(212,167,87,0.28), rgba(212,167,87,0) 70%)",
        }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-40 h-[55vh] w-[55vh] rounded-full opacity-60 blur-3xl bloom"
        style={{
          animationDelay: "-4s",
          background:
            "radial-gradient(closest-side, rgba(29,123,95,0.18), rgba(29,123,95,0) 70%)",
        }}
      />

      <div className="relative mx-auto flex min-h-[92vh] w-full max-w-[1200px] flex-col justify-center px-6 pt-28 pb-16 lg:px-10">
        {/* Wordmark + partner chip */}
        <motion.div
          data-testid="hero-logo-block"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 flex flex-wrap items-center gap-5 sm:mb-10"
        >
          <BrandLogo
            className="h-12 sm:h-16 md:h-20"
            tone="ink"
            testId="hero-logo"
          />
          <motion.a
            href="https://emergent.sh"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-partner-chip"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="group inline-flex items-center gap-2.5 rounded-full border border-[color:var(--emerald)]/30 bg-[color:var(--emerald)]/[0.06] px-4 py-1.5 text-[color:var(--emerald)] transition-all hover:border-[color:var(--emerald)] hover:bg-[color:var(--emerald)]/[0.1]"
          >
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em]">
              {t.hero.partnerChip}
            </span>
            <img
              src="/emergent-logo.svg"
              alt="Emergent"
              className="h-[14px] w-auto"
              draggable={false}
            />
          </motion.a>
        </motion.div>

        {/* Editorial headline */}
        <motion.div style={{ y: titleY }} className="relative">
          <h1
            data-testid="hero-headline"
            className="font-display max-w-[18ch] text-[40px] leading-[1.02] tracking-[-0.03em] text-[color:var(--ink)] sm:text-[56px] md:text-[68px] lg:text-[88px]"
          >
            <MaskLine delay={0.15}>
              <span>{t.hero.title1}</span>
            </MaskLine>
            <span className="mask-line">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.95,
                  delay: 0.32,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{ display: "inline-block" }}
                className="relative"
              >
                <span
                  data-testid="hero-emphasis"
                  className="relative inline-block align-baseline"
                >
                  <span className="font-serif-accent italic text-[color:var(--emerald)]">
                    {t.hero.title_em}
                  </span>
                  <span aria-hidden="true" className="swipe-underline" />
                </span>
              </motion.span>
            </span>
            <MaskLine delay={0.48}>
              <span>{t.hero.title2}</span>
            </MaskLine>
          </h1>
        </motion.div>

        {/* CTA + sub-line */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-14 flex flex-col items-start gap-8 sm:mt-20 sm:flex-row sm:items-end sm:gap-14"
        >
          <a
            href="#contact"
            data-testid="hero-cta-primary"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--emerald)] px-8 py-4 text-sm font-medium text-[color:var(--paper)] transition-all hover:bg-[color:var(--ink)] hover:shadow-[0_18px_40px_-16px_rgba(13,59,46,0.55)]"
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
          <p className="max-w-lg text-[15px] leading-[1.65] text-[color:var(--ink-3)] sm:text-base">
            {t.hero.sub}
          </p>
        </motion.div>
      </div>
    </section>
  );
};
